import React, {useState, useContext, useEffect, useCallback} from 'react'
import {
  FormControl,
  InputLabel,
  MenuItem,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { MessageContext } from './Contexts'
import { useParams } from 'react-router'
import AButton from './AButton'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/AddCircleOutline'
import ActionButtons from './ActionButtons'
import { setModes } from 'modules/Utils'
import { LoadingDotsIcon } from './Icons'

const useStyles = makeStyles(theme => ({
  labelShrunk: {
    display: 'none',
  },
  formControl: {
    backgroundColor: theme.palette.background.default,
    borderRadius: 4,
    margin: '-4px 0',
    minWidth: 200,
  },
  buttonGridItem: {
    paddingTop: 2,
    textAlign: 'right',
  },
  default: {
    fontStyle: 'italic',
    marginLeft: 6,
  },
  select: {
    fontSize: '1rem',
    maxHeight: '40px',
    "& .MuiSelect-select": {
      paddingTop: 10,
      paddingBottom: 10
    },
    paddingTop: "1px"
  },
  headingLabel: {
    fontSize: '1rem',
    top:'-7px',
  },
  groupLabel: {
    fontSize: "16px",
  },
  loadingLabel: {
    fontSize: '1rem',
    top: '-4px',
    minHeight: '40px',
    minWidth: '100px',
  },
  dialogContent: {
    width: 400,
  },
  headingCol: {
    flex: 1.5,
  },
  selectorCol: {
    flex: 2,
  },
  buttonCol: {
    flex: 3,
  },
}))

export default function GroupManager({ getGroups, editGroup, selectedGroupKey, groupName, btnGroupName, groupAttr, groupDisplayAttr, switchGroup, addEditParams={}, hideSelector=false, hideDeleteBtn=false, hideEditBtn=false, labelHeading=null, labelAddBtn=null, labelAddInfo=null, setGroups=null }) {
  const classes = useStyles()
  const { messageDispatch } = useContext(MessageContext)
  const [dialogMethod, setDialogMethod] = useState('')
  const [name, setName] = useState('')
  const [selectedGroup, setSelectedGroup] = useState(null)
  const [groups, setGroupsLocal] = useState([])
  const { dataRoomKey = '' } = useParams()

  const [modeDialog, setModeDialog] = useState('')
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (selectedGroupKey && groups) {
      setSelectedGroup(groups.find(group_i => group_i[groupAttr] === selectedGroupKey))
    } else {
      setSelectedGroup(null)
    }
  }, [selectedGroupKey, groups, groupAttr, setSelectedGroup])

  useEffect(() => {
    setGroupsLocal([])
    setGroups && setGroups([]);
    (async () => {
      setLoading(true)
      const groups = await getGroups()
      setGroupsLocal(groups)
      setGroups && setGroups(groups)
      setLoading(false)
    })()
  }, [getGroups, setGroups])

  useEffect(() => {
    if(!selectedGroup && groups && groups.length === 1) {
      switchGroup(groups[0][groupAttr])
    }
  }, [selectedGroup, groups, switchGroup, groupAttr])

  const openDialog = useCallback((method) => {
    setModeDialog('editing')
    setDialogMethod(method)
    if(method === 'Add') setName('') // clear name field if adding new group
  }, [setModeDialog, setDialogMethod])

  const upsertGroup = useCallback(async options => {
    try {
      setModeDialog('pending')
      const { data } = await editGroup({
        [groupDisplayAttr]: name,
        ...options,
        ...addEditParams,
        data_room: dataRoomKey,
      })
      if (data && data.success) {
        await setModes(setModeDialog, ['completing', 1500, ''])
        switchGroup('') // Necessary so that we unselect on 'Delete' and also when adding new Groups in 'User rights' since that API call does not return the newly added group and we have nothing to select
        const groups = await getGroups()
        setGroupsLocal(groups)
        setGroups && setGroups(groups)
        if (!options.deleted) {
          // After categories have been refreshed from server we can re-select what we had selected
          // This find() is convoluted because sometimes the groupkey exists before (renaming) and sometims after (creating)
          const group = groups.find(
            group_i =>
              group_i[groupAttr] === options[groupAttr] || // Renaming
              (data[groupAttr] && group_i[groupAttr] === data[groupAttr][groupAttr]) // Creating
          )
          if (group) {
            switchGroup(group[groupAttr]) // Necessary so that when we rename or create a group it becomes selected
          }
        }
      } else {
        throw new Error(data && data.info ? data.info : 'An error occurred')
      }
    } catch (error) {
      setModeDialog('')
      messageDispatch({ type: 'warning', payload: error.message })
    }

    setName('')
  }, [dataRoomKey, editGroup, getGroups, groupAttr, groupDisplayAttr, messageDispatch, name, switchGroup, setGroups, addEditParams])

  const fnAction = useCallback(() => {
    switch (dialogMethod) {
      case 'Add':
        upsertGroup({
          data_room: dataRoomKey,
        })
        break

      case 'Edit':
        upsertGroup({
          [groupAttr]: selectedGroup[groupAttr],
        })
        break

      case 'Delete':
        upsertGroup({
          deleted: true,
          [groupAttr]: selectedGroup[groupAttr],
        })
        break

      default:
    }
  }, [dialogMethod, upsertGroup, dataRoomKey, groupAttr, selectedGroup])

  return <>
    <Grid container justifyContent='space-between'>
      <Grid item className={classes.headingCol}>
        <Typography variant='h5'>
          {labelHeading ? labelHeading : groupName}
        </Typography>
      </Grid>

      <Grid item className={classes.selectorCol}>
        <FormControl className={classes.formControl} variant='outlined' margin='dense' style={{display: !hideSelector ? 'inline-flex' : 'none'}}>
          {loading && <InputLabel classes={{root: classes.loadingLabel, shrink: classes.labelShrunk}}><LoadingDotsIcon size={6} vPadding={6} hPadding={6}/></InputLabel>}
          {!loading && !selectedGroupKey && <InputLabel classes={{root: classes.headingLabel, shrink: classes.labelShrunk}}>Select {groupName}</InputLabel>}
          <TextField
            select
            value={selectedGroupKey && groups.length ? selectedGroupKey : ''}
            onChange={({ target: { value } }) => {
              switchGroup(value)
            }}
            className={classes.select}
          >
            {groups.map(type => (
              <MenuItem value={type[groupAttr]} key={type[groupAttr]}>
                <span className={classes.groupLabel}>
                  {type[groupDisplayAttr]}{type.default && <span className={classes.default}>(default)</span>}
                </span>
              </MenuItem>
            ))}
          </TextField>
        </FormControl>
      </Grid>

      <Grid item className={`${classes.buttonGridItem} ${classes.buttonCol}`}>
        <AButton
          startIcon={<AddIcon/>}
          onClick={() => openDialog('Add')}
        >
          {labelAddBtn ? labelAddBtn : `Add ${btnGroupName || groupName}`}
        </AButton>

        {selectedGroup ? <>
          {!hideEditBtn && <AButton
            startIcon={<EditIcon/>}
            onClick={() => {
              setName(selectedGroup[groupDisplayAttr])
              openDialog('Edit')
            }}
          >
            Edit name
          </AButton>}

          {!hideDeleteBtn && <AButton
            startIcon={<DeleteIcon/>}
            onClick={() => openDialog('Delete')}
          >
            Delete {btnGroupName || groupName}
          </AButton>}
        </> : null}
      </Grid>
    </Grid>

    <Dialog open={modeDialog !== ''} aria-labelledby='add-dialog' disableRestoreFocus>
      <DialogTitle>
        {dialogMethod} {groupName}
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        {dialogMethod === 'Add' && labelAddInfo && <Typography variant='body2'>
          {labelAddInfo}
        </Typography>}
        {dialogMethod !== 'Delete' && <TextField
          value={name}
          variant='standard'
          onChange={({ target }) => setName(target.value)}
          onKeyPress={({ key }) => key === 'Enter' && fnAction()}
          autoFocus
          margin='dense'
          label='Name'
          type='text'
          fullWidth
        />}
        {dialogMethod === 'Delete' && <Typography variant='body2'>
          Do you want to delete the {groupName}?
        </Typography>}
      </DialogContent>
      <DialogActions>
        <ActionButtons
          mode={modeDialog}
          setMode={setModeDialog}
          action={fnAction}
          cancel={(e, setMode) => setMode('')}
          actionTitle={dialogMethod}
          colorAction={dialogMethod === 'Delete' ? 'error' : 'primary'}
          neverShowEdit={true}
        />
      </DialogActions>
    </Dialog>
  </>;
}
